<template>
  <v-card flat class="program-summary-card" :style="cardStyle">
    <div
      class="program-summary-card__layout"
      :class="{ 'program-summary-card__layout--mobile': isStacked }"
    >
      <!-- Image Area -->
      <div class="program-card__image-wrapper">
        <v-img
          :src="currentSrc"
          :alt="program.displayName || program.name"
          :aspect-ratio="16 / 9"
          class="program-card__image"
          :class="{ 'program-card__image--archived': resolvedOverlay === 'archived' }"
          @error="handleImageError"
        />
        <div v-if="resolvedOverlay === 'ended'" class="program-card__overlay" />
      </div>

      <!-- Content Area -->
      <div class="program-card__content">
        <div class="program-card__title-block">
          <Typography variant="h-2" as="span" class="program-card__title">
            {{ program.displayName || program.name }}
          </Typography>
          <div v-if="formattedDateRange || statusChip || timeAlert" class="program-card__info-row">
            <div class="program-card__date-row">
              <StatusChip
                v-if="statusChip"
                :variant="statusChip.variant"
                :outlined="statusChip.outlined"
                :text="statusChip.text"
              />
              <template v-if="formattedDateRange">
                <Icon :icon="dateIcon" color="darkGrey" />
                <Typography variant="body-2" as="span" class="darkGrey--text">
                  {{ formattedDateRange }}
                </Typography>
              </template>
            </div>
            <div v-if="timeAlert" class="program-card__date-row">
              <Icon icon="mdi-clock" color="darkGrey" />
              <Typography variant="body-2" as="span" class="darkGrey--text">
                {{ timeAlert }}
              </Typography>
            </div>
          </div>
        </div>

        <template v-if="cardState !== 'coming_soon'">
          <slot name="metric" />
        </template>
        <template v-else>
          <Typography v-if="program.description" variant="body-1">
            {{ program.description }}
          </Typography>
          <Typography v-else variant="body-1"> This program is coming soon! </Typography>
        </template>
        <div class="program-card__actions">
          <slot name="actions" />
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import Icon from "@/components/generic/Icon.vue";
import StatusChip from "@/components/generic/StatusChip.vue";
import moment from "moment";

const STATUS_CHIPS = {
  coming_soon: { variant: "info", outlined: true, text: "Coming Soon!" },
  completed: { variant: "default", outlined: true, text: "Ended" },
  inactive: { variant: "dark", outlined: false, text: "Archived" },
};

export default {
  name: "ProgramSummaryCardLayout",
  components: { Typography, Icon, StatusChip },
  props: {
    program: {
      type: Object,
      required: true,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    draft: {
      type: Boolean,
      default: false,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    cardState: {
      type: String,
      default: undefined,
      validator: (value) => Object.keys(STATUS_CHIPS).includes(value),
    },
    overlay: {
      type: Boolean,
      default: true,
    },
    timeAlert: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      currentSrc: "",
    };
  },
  created() {
    this.currentSrc = this.imageSrc;
  },
  watch: {
    imageSrc(newVal) {
      this.currentSrc = newVal;
    },
  },
  computed: {
    cardStyle() {
      return this.preview && this.draft ? { backgroundColor: "#F6F5F6" } : {};
    },
    isStacked() {
      return this.stacked || this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm;
    },
    imageSrc() {
      return (
        this.program.imageURL || this.program.imageUrl || require("@/assets/placeholder_image.png")
      );
    },
    statusChip() {
      return STATUS_CHIPS[this.cardState] || null;
    },
    dateIcon() {
      return this.cardState === "coming_soon" ? "mdi-calendar-clock" : "mdi-calendar-blank";
    },
    resolvedOverlay() {
      if (!this.overlay) return "none";
      if (this.cardState === "inactive") return "archived";
      return this.cardState === "completed" ? "ended" : "none";
    },
    formattedDateRange() {
      const formatDate = (dateStr) => {
        if (!dateStr) return "";
        // Local calendar day so PAX matches the builder (ADO 189757).
        const m = moment(dateStr);
        return m.isValid() ? m.format("MMM D, YYYY") : "";
      };
      const start = formatDate(this.program.startDate);
      const end = formatDate(this.program.endDate);
      if (!start) return "";
      if (this.cardState === "coming_soon") return `Starts ${start}`;
      return end ? `${start} \u2013 ${end}` : start;
    },
  },
  methods: {
    handleImageError() {
      const placeholder = require("@/assets/placeholder_image.png");
      if (this.currentSrc !== placeholder) {
        this.currentSrc = placeholder;
      }
    },
  },
};
</script>

<style scoped>
.program-summary-card {
  border: none !important;
  border-radius: 0 !important;
  background: #ffffff;
  width: 100%;
}

.program-summary-card__layout {
  display: flex;
  flex-direction: row;
  padding: 40px 32px;
  gap: 32px;
  align-items: center;
}

.program-summary-card__layout--mobile {
  flex-direction: column;
  padding: 24px 24px 32px;
  gap: 16px;
}

.program-card__image-wrapper {
  position: relative;
  flex: 1 0 0;
  max-width: 538px;
  max-height: 300px;
}

.program-summary-card__layout--mobile .program-card__image-wrapper {
  flex: none;
  max-width: 100%;
  max-height: none;
  width: 100%;
}

.program-card__image {
  border-radius: 8px;
  display: block;
  width: 100%;
}

.program-card__image--archived {
  filter: grayscale(1);
}

.program-card__overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  border-radius: 8px;
  z-index: 1;
}

.program-card__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 32px;
  min-width: 0;
  align-items: flex-start;
}

.program-summary-card__layout--mobile .program-card__content {
  width: 100%;
  align-items: center;
}

.program-card__title-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.program-card__title {
  word-break: break-word;
  text-align: left;
}

.program-card__info-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
}

.program-card__date-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.program-card__actions {
  display: flex;
  gap: 16px;
}
</style>
